import * as React from "react"
import _ from "lodash"
import en from "../../../i18n/en"
import { Appearance, ViewStyle } from "react-native"
import { FastField, FastFieldProps, FieldProps } from "formik"
import { FormControl, FormControlProps } from "../../form-control/form-control"
import { translate, TxKeyPath } from "../../../i18n"
import { observer } from "mobx-react-lite"
import { color } from "../../../theme"
import { IRadioGroupProps, IRadioProps, Radio } from "native-base"
import { FC, useCallback } from "react"

export interface RadioFieldProps {
  FieldComponent?: React.ComponentType<any>,
  name: string,
  colorScheme?: "light" | "dark",
  txPrefix?: keyof typeof en,
  formControlProps?: FormControlProps,
  radioGroupProps?: Omit<IRadioGroupProps, "name">,
  radios?: Array<IRadioProps>,
  castValue?: (value: string) => string
  isHorizontal?: boolean
}

const FORM_CONTROL: ViewStyle = {
  paddingHorizontal: 16,
  // marginBottom: 8,
  paddingBottom: 8,
  backgroundColor: color.palette.white
}

const defaultRadios: any = []

const RadioFormControl: FC<Omit<RadioFieldProps, "FieldComponent"|"name"> & (FastFieldProps | FieldProps)> = observer(function RadioFormControl(props) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
    txPrefix,
    formControlProps, radioGroupProps, radios = defaultRadios, castValue = (v) => v,
    isHorizontal = false,
    field, form: { setFieldValue, setFieldTouched }, meta
  } = props;

  const column = field.name.split('.').pop()

  const onChange = useCallback(async (value) => {
    await setFieldValue(field.name, castValue(value))
    await setFieldTouched(field.name)
  }, [castValue, field.name, setFieldTouched, setFieldValue])

  return (<FormControl
    isInvalid={(meta.initialValue || meta.touched) && !!meta.error}
    labelTx={(txPrefix && `${txPrefix}.${_.camelCase(column)}` as TxKeyPath) || undefined}
    colorScheme={colorScheme}
    labelColor={colorScheme === "light" ? color.palette.gray800 : color.palette.gray200}
    errorMessage={meta.error}
    style={FORM_CONTROL}
    {...formControlProps}
  >
    <Radio.Group
      colorScheme={colorScheme}
      name={field.name}
      accessibilityLabel={(txPrefix && translate(`${txPrefix}.${_.camelCase(column)}` as TxKeyPath)) || undefined}
      defaultValue={`${meta.initialValue}`}
      value={`${field.value}`}
      onChange={onChange}
      flexDirection={isHorizontal? "row" : "column"}
      {...radioGroupProps}
    >
      {radios.map((radioProps, index) => (
        <Radio
          key={`radio-${field.name}-${index}`}
          mr={index < (radios.length - 1) ? "32px" : undefined}
          mb={index < (radios.length - 1) ? "8px" : undefined}
          {...radioProps}
        />
      ))}
    </Radio.Group>
  </FormControl>);
})

/**
 * Describe your component here
 */
export const RadioField = observer(function RadioField(props: RadioFieldProps) {
  const {
    FieldComponent = FastField,
    name, colorScheme = "light" || Appearance.getColorScheme(),
  } = props

  return (
    <FieldComponent name={name}>
      {(fieldProps) => (
        <RadioFormControl colorScheme={colorScheme} {...props} {...fieldProps} />
      )}
    </FieldComponent>
  )
})
